import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { FileText, Code, Download, ExternalLink } from 'lucide-react';
import '../styles/Pages.css';
import { Link } from 'react-router-dom';

const Documentation = () => {
  const docSections = [
    {
      title: 'Guías de Usuario',
      description: 'Documentación completa para usuarios finales',
      items: [
        'Manual de Usuario - Plataforma Web',
        'Manual de Usuario - Aplicación Móvil',
        'Guía de Instalación de Dispositivos',
        'Configuración de Alertas y Notificaciones'
      ]
    },
    {
      title: 'Documentación Técnica',
      description: 'Información técnica para desarrolladores e integradores',
      items: [
        'Referencia de API REST',
        'Protocolos de Comunicación',
        'Especificaciones de Dispositivos',
        'Guía de Integración con Terceros'
      ]
    },
    {
      title: 'Recursos para Administradores',
      description: 'Documentación para administradores de sistema',
      items: [
        'Gestión de Usuarios y Permisos',
        'Configuración de Seguridad',
        'Administración de Flotas',
        'Reportes Avanzados'
      ]
    }
  ];

  const apiEndpoints = [
    {
      name: 'GET /api/vehicles',
      description: 'Obtiene la lista de vehículos',
      parameters: 'status, group_id, limit, offset',
      authentication: 'API Key, Bearer Token'
    },
    {
      name: 'GET /api/vehicles/{id}',
      description: 'Obtiene información detallada de un vehículo',
      parameters: 'id (requerido)',
      authentication: 'API Key, Bearer Token'
    },
    {
      name: 'GET /api/locations',
      description: 'Obtiene ubicaciones de vehículos',
      parameters: 'vehicle_id, from_date, to_date, limit',
      authentication: 'API Key, Bearer Token'
    },
    {
      name: 'POST /api/geofences',
      description: 'Crea una nueva geocerca',
      parameters: 'name, description, coordinates, type',
      authentication: 'API Key, Bearer Token'
    },
    {
      name: 'GET /api/alerts',
      description: 'Obtiene alertas generadas',
      parameters: 'vehicle_id, type, status, from_date, to_date',
      authentication: 'API Key, Bearer Token'
    }
  ];

  return (
    <div className="page-container">
      <Header scrolled={true} />
      
      <main className="page-content">
        <section className="page-hero">
          <div className="container">
            <h1>Documentación</h1>
            <p>Recursos completos para usuarios, administradores y desarrolladores</p>
          </div>
        </section>
        
        <section className="page-section">
          <div className="container">
            <div className="doc-intro">
              <h2>Documentación de VillaTracking</h2>
              <p>Bienvenido a la documentación oficial de VillaTracking. Aquí encontrarás guías detalladas, tutoriales y referencias técnicas para ayudarte a aprovechar al máximo nuestra plataforma de rastreo vehicular.</p>
              <p>Selecciona la sección que necesites consultar o utiliza la búsqueda para encontrar información específica.</p>
            </div>
            
            <div className="doc-sections">
              {docSections.map((section, index) => (
                <div className="doc-section" key={index}>
                  <h3>{section.title}</h3>
                  <p>{section.description}</p>
                  <ul className="doc-list">
                    {section.items.map((item, itemIndex) => (
                      <li key={itemIndex}>
                        <a href={`/documentation/${section.title.toLowerCase().replace(/ /g, '-')}/${itemIndex + 1}`}>
                          <FileText size={16} />
                          <span>{item}</span>
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>
        
        <section className="page-section page-section-alt">
          <div className="container">
            <h2>Referencia de API</h2>
            <p className="section-description">
              VillaTracking ofrece una API RESTful completa que permite integrar nuestras funcionalidades en tus propias aplicaciones y sistemas.
            </p>
            
            <div className="api-info">
              <div className="api-details">
                <div className="api-detail">
                  <h4>Base URL</h4>
                  <div className="code-block">
                    <code>https://api.villatracking.com/v1</code>
                  </div>
                </div>
                
                <div className="api-detail">
                  <h4>Autenticación</h4>
                  <p>La API utiliza autenticación mediante API Key o Bearer Token. Todas las solicitudes deben incluir uno de estos métodos de autenticación.</p>
                </div>
                
                <div className="api-detail">
                  <h4>Formatos de Respuesta</h4>
                  <p>La API devuelve respuestas en formato JSON. Los errores siguen un formato estándar con códigos de estado HTTP apropiados.</p>
                </div>
              </div>
              
              <div className="api-endpoints">
                <h4>Endpoints Principales</h4>
                <div className="endpoints-table">
                  <div className="endpoint-header">
                    <div className="endpoint-name">Endpoint</div>
                    <div className="endpoint-description">Descripción</div>
                    <div className="endpoint-params">Parámetros</div>
                    <div className="endpoint-auth">Autenticación</div>
                  </div>
                  
                  {apiEndpoints.map((endpoint, index) => (
                    <div className="endpoint-row" key={index}>
                      <div className="endpoint-name">
                        <code>{endpoint.name}</code>
                      </div>
                      <div className="endpoint-description">{endpoint.description}</div>
                      <div className="endpoint-params">{endpoint.parameters}</div>
                      <div className="endpoint-auth">{endpoint.authentication}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="api-cta">
              <a href="/documentation/api" className="btn btn-primary">
                Ver Documentación Completa de API
              </a>
              <a href="/documentation/api/examples" className="btn btn-secondary">
                <Code size={16} />
                <span>Ejemplos de Código</span>
              </a>
            </div>
          </div>
        </section>
        
        <section className="page-section">
          <div className="container">
            <h2>Descargas</h2>
            <div className="downloads-grid">
              <div className="download-card">
                <div className="download-icon">
                  <FileText size={32} />
                </div>
                <h3>Manual de Usuario</h3>
                <p>Guía completa para usuarios finales de la plataforma VillaTracking.</p>
                <a href="/downloads/manual-usuario.pdf" className="download-button">
                  <Download size={16} />
                  <span>Descargar PDF</span>
                </a>
              </div>
              
              <div className="download-card">
                <div className="download-icon">
                  <FileText size={32} />
                </div>
                <h3>Guía de Instalación</h3>
                <p>Instrucciones detalladas para la instalación de dispositivos GPS.</p>
                <a href="/downloads/guia-instalacion.pdf" className="download-button">
                  <Download size={16} />
                  <span>Descargar PDF</span>
                </a>
              </div>
              
              <div className="download-card">
                <div className="download-icon">
                  <Code size={32} />
                </div>
                <h3>SDK para Desarrolladores</h3>
                <p>Kit de desarrollo para integrar VillaTracking en aplicaciones personalizadas.</p>
                <a href="/downloads/sdk-villatracking.zip" className="download-button">
                  <Download size={16} />
                  <span>Descargar ZIP</span>
                </a>
              </div>
              
              <div className="download-card">
                <div className="download-icon">
                  <ExternalLink size={32} />
                </div>
                <h3>Aplicación Móvil</h3>
                <p>Aplicación oficial de VillaTracking para iOS y Android.</p>
                <div className="app-download-buttons">
                  <a href="https://apps.apple.com" target="_blank" rel="noopener noreferrer" className="app-button">
                    App Store
                  </a>
                  <a href="https://play.google.com" target="_blank" rel="noopener noreferrer" className="app-button">
                    Google Play
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        <section className="page-cta">
          <div className="container">
            <h2>¿Necesitas ayuda personalizada?</h2>
            <p>Nuestro equipo de soporte está listo para asistirte</p>
            <Link to="/#contact" className="btn btn-primary btn-lg">
              Contactar Soporte
            </Link>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default Documentation;